<template>
  <div class="notification-setting">
    <div
      v-for="{ channel, channelHeader, categorySettings } in settingsGroup"
      :key="channel"
      class="notification-setting-group">
      <h4>{{ $t(channelHeader) }}</h4>

      <div v-for="setting in categorySettings" :key="setting.category" class="notification-setting-item">
        <div>{{ $t(setting.categoryTitle) }}</div>
        <BackstageSwitch
          :model-value="setting.modelValue"
          :on-toggle="(value) => onToggle(setting.category, channel, value)" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { merge } from 'lodash';
import { computed, onMounted, reactive } from 'vue';

import BackstageSwitch from '@/components/common/BackstageSwitch.vue';
import { NotificationCategory, NotificationChannel } from '@/models/notification.enum';
import { type SettingsGroupItem } from '@/models/notification.model';
import { useUserStore, type SubscriptionPatch } from '@/stores/user';
import type { SubscriptionsModel } from '@onrunning/backstage-models';

const enableShipmentEmailNotification = import.meta.env.VITE_APP_enableShipmentEmailNotification === 'true';

const { userProfile, setUserNotificationSubscription } = useUserStore();

const notificationSettings = reactive<SubscriptionsModel>({
  orderShipment: { push: false, email: false },
  orderUpdates: { push: false, email: false },
  payments: { email: false },
});

const settingsGroup = computed<SettingsGroupItem[]>(() => [
  {
    channel: NotificationChannel.Push,
    channelHeader: 'header.notification.channel_header.in_app',
    categorySettings: [
      {
        category: NotificationCategory.OrderShipment,
        categoryTitle: 'header.notification.settings.order_shipped',
        modelValue: notificationSettings.orderShipment.push,
      },
    ],
  },
  ...(enableShipmentEmailNotification
    ? [
        {
          channel: NotificationChannel.Email,
          channelHeader: 'header.notification.channel_header.email',
          categorySettings: [
            {
              category: NotificationCategory.OrderShipment,
              categoryTitle: 'header.notification.settings.order_shipped',
              modelValue: notificationSettings.orderShipment.email,
            },
          ],
        },
      ]
    : []),
]);

const onToggle = async (category: NotificationCategory, channel: NotificationChannel, value: boolean) => {
  const previousValue = notificationSettings[category][channel];
  notificationSettings[category][channel] = value;

  try {
    const patch = { [category]: { [channel]: value } } as SubscriptionPatch;
    await setUserNotificationSubscription(patch);
  } catch {
    notificationSettings[category][channel] = previousValue;
  }
};

onMounted(() => {
  const existingNotificationSetting = userProfile.user.notificationSubscriptions;
  if (existingNotificationSetting) {
    merge(notificationSettings, existingNotificationSetting);
  }
});
</script>

<style scoped lang="scss">
.notification-setting {
  &-group {
    margin-bottom: 1.5rem;

    h4 {
      margin-bottom: 0.75rem;
    }
  }

  &-item {
    @include center-flex;
    justify-content: space-between;
  }
}
</style>
